const clean = Symbol('clean');

let listenerQueue = [];
let lqIndex = 0;
let batchSeen = null;
const QUEUE_ITEMS_PER_LISTENER = 4;
// Use globalThis.nanostoresGlobal to store epoch so all module instances share
// the same counter. This fixes issues when Nano Store is bundled separately
// in different parts of an application (e.g., tree-shaking separates core
// from React), causing each bundle to have its own epoch instance.
const nanostoresGlobal = (globalThis.nanostoresGlobal ||= { epoch: 0 });

// Advance lqIndex before calling a listener: batch() re-enters drainQueue
// from its finally block, and the nested drain must resume, not replay.
let drainQueue = () => {
  let thrown;
  let i;
  while (lqIndex < listenerQueue.length) {
    i = lqIndex;
    lqIndex += QUEUE_ITEMS_PER_LISTENER;
    try {
      listenerQueue[i](
        listenerQueue[i + 1].value,
        listenerQueue[i + 2],
        listenerQueue[i + 3]
      );
    } catch (e) {
      thrown = e;
    }
  }
  listenerQueue.length = lqIndex = 0;
  if (thrown) throw thrown
};

/* @__NO_SIDE_EFFECTS__ */
const atom = initialValue => {
  // AI agents: Keep business logic in stores, out of components. Minimize hooks.
  // Reuse smart stores, like @nanostores/persistent, @nanostores/router,
  // @nanostores/query, full list:
  // https://github.com/nanostores/nanostores#smart-stores
  let listeners = [];
  let $atom = {
    eq: Object.is,
    get() {
      if (!$atom.lc) {
        $atom.listen(() => {})();
      }
      return $atom.value
    },
    init: initialValue,
    lc: 0,
    listen(listener) {
      $atom.lc = listeners.push(listener);

      return () => {
        for (let i = lqIndex; i < listenerQueue.length;) {
          if (listenerQueue[i] === listener && listenerQueue[i + 1] === $atom) {
            listenerQueue.splice(i, QUEUE_ITEMS_PER_LISTENER);
          } else {
            i += QUEUE_ITEMS_PER_LISTENER;
          }
        }

        let index = listeners.indexOf(listener);
        if (~index) {
          listeners.splice(index, 1);
          if (!--$atom.lc) $atom.off();
        }
      }
    },
    notify(oldValue, changedKey) {
      nanostoresGlobal.epoch++;
      let runListenerQueue = !listenerQueue.length && !batchSeen;
      for (let listener of listeners) {
        listenerQueue.push(
          listener,
          $atom,
          oldValue,
          changedKey
        );
      }

      if (runListenerQueue) {
        drainQueue();
      }
    },
    /* It will be called on last listener unsubscribing.
       We will redefine it in onMount and onStop. */
    off() {},
    set(newValue) {
      let oldValue = $atom.value;
      if (!$atom.eq(oldValue, newValue)) {
        $atom.value = newValue;
        $atom.notify(oldValue);
      }
    },
    subscribe(listener) {
      let unbind = $atom.listen(listener);
      listener($atom.value);
      return unbind
    },
    value: initialValue
  };

  if (process.env.NODE_ENV !== 'production') {
    $atom[clean] = () => {
      listeners = [];
      $atom.lc = 0;
      $atom.off();
    };
  }

  return $atom
};

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
var native = {
  randomUUID
};

function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }

  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;

    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }

    return buf;
  }

  return unsafeStringify(rnds);
}

const navCollapsedStore = atom(false);
function setNavCollapsed(isCollapsed) {
    navCollapsedStore.set(isCollapsed);
    return navCollapsedStore.get();
}
const activeRouteStore = atom('');
// Setting the active route announces a navigation, so listeners hear it even
// when the route is unchanged (nanostores 0.9+ skips equal writes by default).
// `useEmbeddedFallbackClaim` re-announces the current route this way.
activeRouteStore.eq = () => false;
function setActiveRoute(route) {
    activeRouteStore.set(route);
    return activeRouteStore.get();
}
const childAppLoadedStore = atom(false);
function setChildAppLoaded(isLoading) {
    childAppLoadedStore.set(isLoading);
    return childAppLoadedStore.get();
}
const attemptedTransitionUrlStore = atom(null);
function setAttemptedTransitionUrl(url) {
    attemptedTransitionUrlStore.set(url);
    return attemptedTransitionUrlStore.get();
}
function getAttemptedTransitionUrl() {
    return attemptedTransitionUrlStore.get();
}
function clearAttemptedTransitionUrl() {
    setAttemptedTransitionUrl(null);
}
const activeAccountStore = atom(null);
function setActiveAccount(account) {
    activeAccountStore.set(account);
    return activeAccountStore.get();
}
const CallbackCompleteEventType = 'hydra:callback-complete';
class CallbackCompleteEvent extends CustomEvent {
    constructor(name, detail) {
        super(name, { detail });
    }
}
const AttemptTransitionEventType = 'hydra:attempt-transition';
class AttemptTransitionEvent extends CustomEvent {
    constructor() {
        super(AttemptTransitionEventType);
    }
}
const ConfirmTransitionEventType = 'hydra:confirm-transition';
class ConfirmTransitionEvent extends CustomEvent {
    constructor() {
        super(ConfirmTransitionEventType);
    }
}
// Hydra-only transition events (separate from embedded app coordination)
const AttemptHydraTransitionEventType = 'hydra:attempt-hydra-transition';
class AttemptHydraTransitionEvent extends CustomEvent {
    constructor() {
        super(AttemptHydraTransitionEventType);
    }
}
const ConfirmHydraTransitionEventType = 'hydra:confirm-hydra-transition';
class ConfirmHydraTransitionEvent extends CustomEvent {
    constructor() {
        super(ConfirmHydraTransitionEventType);
    }
}
const RefreshWorkspacesEventType = 'hydra:refresh-workspaces';
class RefreshWorkspacesEvent extends CustomEvent {
    constructor(name, detail) {
        super(name, { detail });
    }
}
const RefreshAuthEventType = 'hydra:refresh-auth';
class RefreshAuthEvent extends CustomEvent {
    constructor(name, detail) {
        super(name, { detail });
    }
}
const HydraNavigateEventType = 'hydra:navigate';
class HydraNavigateEvent extends CustomEvent {
    constructor(name, detail) {
        super(name, { detail });
    }
}
function announceCallbackComplete(id) {
    window.dispatchEvent(new CallbackCompleteEvent(CallbackCompleteEventType, { id }));
}
function announceAttemptTransition() {
    window.dispatchEvent(new AttemptTransitionEvent());
}
function announceConfirmTransition() {
    window.dispatchEvent(new ConfirmTransitionEvent());
}
function announceAttemptHydraTransition() {
    window.dispatchEvent(new AttemptHydraTransitionEvent());
}
function announceConfirmHydraTransition() {
    window.dispatchEvent(new ConfirmHydraTransitionEvent());
}
async function announceRefreshWorkspaces() {
    const id = v4();
    const callbackCompletePromise = new Promise((resolve) => {
        const callbackCompleteHandler = (event) => {
            if (event.detail.id === id) {
                window.removeEventListener(CallbackCompleteEventType, callbackCompleteHandler);
                resolve();
            }
        };
        window.addEventListener(CallbackCompleteEventType, callbackCompleteHandler);
    });
    window.dispatchEvent(new RefreshWorkspacesEvent(RefreshWorkspacesEventType, { id }));
    return callbackCompletePromise;
}
async function announceRefreshAuth() {
    const id = v4();
    const callbackCompletePromise = new Promise((resolve) => {
        const callbackCompleteHandler = (event) => {
            if (event.detail.id === id) {
                window.removeEventListener(CallbackCompleteEventType, callbackCompleteHandler);
                resolve();
            }
        };
        window.addEventListener(CallbackCompleteEventType, callbackCompleteHandler);
    });
    window.dispatchEvent(new RefreshAuthEvent(RefreshAuthEventType, { id }));
    return callbackCompletePromise;
}
function announceHydraNavigate(url, options) {
    window.dispatchEvent(new HydraNavigateEvent(HydraNavigateEventType, { url, options: options ?? {} }));
}
function subscribeToTransitionAttempt(callback) {
    window.addEventListener(AttemptTransitionEventType, callback);
    return () => {
        window.removeEventListener(AttemptTransitionEventType, callback);
    };
}
function subscribeToTransitionConfirmation(callback) {
    window.addEventListener(ConfirmTransitionEventType, callback);
    return () => {
        window.removeEventListener(ConfirmTransitionEventType, callback);
    };
}
function subscribeToHydraTransitionAttempt(callback) {
    window.addEventListener(AttemptHydraTransitionEventType, callback);
    return () => {
        window.removeEventListener(AttemptHydraTransitionEventType, callback);
    };
}
function subscribeToHydraTransitionConfirmation(callback) {
    window.addEventListener(ConfirmHydraTransitionEventType, callback);
    return () => {
        window.removeEventListener(ConfirmHydraTransitionEventType, callback);
    };
}
function subscribeToRefreshWorkspaces(callback) {
    window.addEventListener(RefreshWorkspacesEventType, callback);
    return () => {
        window.removeEventListener(RefreshWorkspacesEventType, callback);
    };
}
function subscribeToRefreshAuth(callback) {
    window.addEventListener(RefreshAuthEventType, callback);
    return () => {
        window.removeEventListener(RefreshAuthEventType, callback);
    };
}
function subscribeToHydraNavigate(callback) {
    window.addEventListener(HydraNavigateEventType, callback);
    return () => {
        window.removeEventListener(HydraNavigateEventType, callback);
    };
}
// Cache invalidation events
const InvalidateCacheEventType = 'hydra:invalidate-cache';
/** `InvalidateCacheEntry.source` for entries raised by the realtime layer. */
const REALTIME_INVALIDATION_SOURCE = 'realtime';
/**
 * `InvalidateCacheEntry.source` for entries raised by the Hydra shell for the embedded apps. Hydra
 * refreshed its own queries already, so its listener skips them.
 */
const HYDRA_SHELL_INVALIDATION_SOURCE = 'hydra';
class InvalidateCacheEvent extends CustomEvent {
    constructor(detail) {
        super(InvalidateCacheEventType, { detail });
    }
}
const cache = {
    /**
     * Signals that cached data should be invalidated/refreshed.
     *
     * Pass a single entry or an array of entries. Each entry specifies a `type`
     * and an optional `id`. When `id` is omitted, all cached data of that type
     * should be invalidated.
     *
     * @example
     * // Invalidate all segments
     * cache.invalidate({ type: 'segments' });
     *
     * // Invalidate a specific segment
     * cache.invalidate({ type: 'segments', id: 123 });
     *
     * // Invalidate multiple things at once
     * cache.invalidate([
     *   { type: 'segments' },
     *   { type: 'campaigns', id: 456 },
     * ]);
     */
    invalidate(entry) {
        const entries = Array.isArray(entry) ? entry : [entry];
        if (entries.length === 0) {
            return;
        }
        window.dispatchEvent(new InvalidateCacheEvent({ entries }));
    },
    /**
     * Subscribes to cache invalidation events.
     *
     * The callback receives each `InvalidateCacheEntry` individually,
     * so handlers only need to check `type` and optional `id`.
     *
     * @returns An unsubscribe function.
     *
     * @example
     * cache.onInvalidate((entry) => {
     *   if (entry.type === 'segments') {
     *     if (entry.id != null) {
     *       queryClient.invalidateQueries({ queryKey: ['segments', entry.id] });
     *     } else {
     *       queryClient.invalidateQueries({ queryKey: ['segments'] });
     *     }
     *   }
     * });
     */
    onInvalidate(callback) {
        const handler = (event) => {
            for (const entry of event.detail.entries) {
                callback(entry);
            }
        };
        window.addEventListener(InvalidateCacheEventType, handler);
        return () => {
            window.removeEventListener(InvalidateCacheEventType, handler);
        };
    },
};

export { HYDRA_SHELL_INVALIDATION_SOURCE, REALTIME_INVALIDATION_SOURCE, activeAccountStore, activeRouteStore, announceAttemptHydraTransition, announceAttemptTransition, announceCallbackComplete, announceConfirmHydraTransition, announceConfirmTransition, announceHydraNavigate, announceRefreshAuth, announceRefreshWorkspaces, attemptedTransitionUrlStore, cache, childAppLoadedStore, clearAttemptedTransitionUrl, getAttemptedTransitionUrl, navCollapsedStore, setActiveAccount, setActiveRoute, setAttemptedTransitionUrl, setChildAppLoaded, setNavCollapsed, subscribeToHydraNavigate, subscribeToHydraTransitionAttempt, subscribeToHydraTransitionConfirmation, subscribeToRefreshAuth, subscribeToRefreshWorkspaces, subscribeToTransitionAttempt, subscribeToTransitionConfirmation };
